import {
  Player,
  TrackInfo,
  TrackType,
  AdPlayer,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  Action,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  PlaybackOptions,
  AdComposedPlayer,
  PlayerContext,
  PLAYBACK_STATES,
  TrackVariantInfo,
  AdTechnology,
} from '@quickplay/player-interface';
import {
  Error,
  Logger,
  createEventEmitter,
  EventEmitter,
  AnyFunc,
  eventObject,
  TimeoutController,
  createSafeTimeout,
  PlatformUtil,
} from '@quickplay/foundation';
import { PlayerBuilder } from '../../PlayerBuilder';
import { createIMAAdPlayer } from '../IMAAdsPlayer';
import {
  createComposableAFCPlaybackSession,
  emitContentPlayerCurrentState,
  isAdComposedPlayer,
} from '../util/ComposablePlayerUtils';
import { AdIncomingNotifier, AFCPlaybackSession } from '@quickplay/player';

/**
 * Creates an `AdPlayer` by composing content `Player` and internally created
 * Ad `Player`.
 *
 * The newly created composed `Player` manages the complexities of Ad loading,
 * Ad Management, switching the context between content and ad, etc.
 *
 * @param builder PlayerBuilder Ad PlayerBuilder instance.
 * @returns AdPlayer A new player instance that composes Ad and Content Players
 */
export function createComposableAdPlayer(builder: PlayerBuilder): AdComposedPlayer {
  const timeoutController: TimeoutController = createSafeTimeout();
  const logger: Logger = builder.configuredLogger;
  logger.log('[ComposableAdPlayer] Initializing Composable Ad Player');
  const eventEmitter: EventEmitter = createEventEmitter();
  let afcPlaybackSession: AFCPlaybackSession | undefined;
  let adIncomingNotifier: AdIncomingNotifier | undefined;

  // Extract the contentPlayer instance passed-in to the `PlayerBuilder`
  const contentPlayer: Player = builder.player;

  // Dev Note:
  // Smart TV platforms often require a delay before content playback
  // This delay is a platform-specific optimization that improves playback reliability with out an error.
  // Default value is set to 500ms, but can be overridden by the `smartTvAdPlaybackOptions.contentPlaybackDelayMs` property in the builder.
  const smartTvPlaybackDelayMs: number = builder?.smartTvAdPlaybackOptions?.contentPlaybackDelayMs || 500;

  // Factor to extend the base delay (above) specifically for mid-roll to content transitions
  // Dev Note: Mid-rolls often introduce additional rendering delays vs. pre-rolls, especially on resource-constrained platforms.
  // This factor allows developers to dynamically scale the delay (e.g., 1.0 = base, 1.5 = 50% more) based on device testing results.
  // Can be configured per platform to balance responsiveness vs. transition stability.
  // Default value is 1, can be overriden with smartTvAdPlaybackOptions?.adToContentTransitionFactor.
  const adToContentTransitionFactor: number = builder?.smartTvAdPlaybackOptions?.adToContentTransitionFactor || 1;

  const getAutoPlayPreference = (): boolean => {
    return contentPlayer.playbackOptions ? contentPlayer.playbackOptions.autoPlayOnLoad : false;
  };
  const setAutoPlayOptionToContentPlayer = (autoPlayOnload: boolean): void => {
    if (contentPlayer.playbackOptions) {
      contentPlayer.setPlaybackOptions({
        ...contentPlayer.playbackOptions,
        autoPlayOnLoad: autoPlayOnload,
      });
    }
  };
  const disableAutoPlayOnLoad = (): void => {
    setAutoPlayOptionToContentPlayer(false);
  };
  let autoPlayPreference = getAutoPlayPreference();

  // Note:
  // Disabling autoPlay to stop main content playback during pre-roll ads.
  disableAutoPlayOnLoad();

  // create the `AdPlayer` based on the builder params
  const adPlayer: AdPlayer = createIMAAdPlayer(builder);

  /**
   * Provides access to the internal player context.
   * @property {PlayerContext} internalPlayerContext
   */
  let internalPlayerContext: PlayerContext = PlayerContext.MAIN;

  let contentCompleted = false;
  let contentStarted = false;

  const loadPlayers = (): void => {
    if (autoPlayPreference) {
      //Note:[QPS-2147] ad player loaded subject to autoPlayOnLoad preferences.
      adPlayer.load();
    }

    // Note: [VCI-8503] Deferring contentPlayer loading until preroll gets completed.
    !builder.deferContentLoad && contentPlayer.load();
  };

  const isAdContext = (): boolean => {
    return internalPlayerContext === PlayerContext.AD;
  };

  const switchToContext = (context: PlayerContext): void => {
    internalPlayerContext = context;
    logger.info(`Switched to context: ${internalPlayerContext}`);
    eventEmitter.publish('playercontextchanged', internalPlayerContext);
  };

  const switchToAdContext = (): void => {
    switchToContext(PlayerContext.AD);
  };

  const switchToMainContext = (): void => {
    switchToContext(PlayerContext.MAIN);
  };

  const enableAutoPlayOnLoad = (): void => {
    setAutoPlayOptionToContentPlayer(true);
  };

  const resetContentComplete = (): void => {
    contentCompleted = true;
  };

  /**
   * Handles content playback with platform-specific behavior
   *
   * This function manages the playback initiation based on the platform type.
   * For Smart TVs, it adds a delay before starting playback to improve performance
   * and compatibility. For other platforms, it starts playback immediately.
   *
   * @returns {void} No return value
   */
  const handleContentPlay = (): void => {
    if (PlatformUtil.isSmartTV()) {
      logger.debug(`[IMA CSAI][Smart TV] Content resumed requested to play after ad break`);
      timeoutController.set(() => {
        contentPlayer.play();
      }, smartTvPlaybackDelayMs * adToContentTransitionFactor);
    } else {
      contentPlayer.play();
    }
  };

  /**
   * Checks if the play operation should be skipped based on player states.
   * For play operation, we should allow play when players are in PAUSED or LOADED state.
   *
   * @returns true if play operation should be skipped, false if players are ready for play
   */
  const shouldSkipPlayOperation = (): boolean => {
    if (
      contentPlayer.playerState !== PLAYBACK_STATES.PAUSED &&
      contentPlayer.playerState !== PLAYBACK_STATES.LOADED &&
      contentPlayer.playerState !== PLAYBACK_STATES.STARTED
    ) {
      logger.debug('Skipping play operation - content player state is not in paused, loaded, or started state');
      return true;
    }

    return false;
  };

  /**
   * Checks if the pause operation should be skipped based on player states.
   * For pause operation, we should only proceed if players are in STARTED state.
   *
   * @returns true if pause operation should be skipped, false if players are ready for pause
   */
  const shouldSkipPauseOperation = (): boolean => {
    if (contentPlayer.playerState !== PLAYBACK_STATES.STARTED || adPlayer.playerState !== PLAYBACK_STATES.STARTED) {
      logger.debug('Skipping pause operation - content/ad player state is not in started state');
      return true;
    }
    return false;
  };

  /**
   * Handles content resume after a mid-roll or pre-roll ad break.
   *
   * On WPE/STB devices, mid-roll resumes (currentTime > 0) are deferred to SmartTvAdManager
   * to avoid a race condition between IMA SDK teardown and the WPE media pipeline
   * re-initialization. All other cases call handleContentPlay() directly.
   */
  const handleMidrollOrPrerollResume = (): void => {
    const shouldDeferWpeMidrollResume = PlatformUtil.isWPE() && contentPlayer.currentTime > 0;
    if (shouldDeferWpeMidrollResume) {
      logger.debug(
        '[IMA CSAI][Smart TV][WPE] Deferring content play to SmartTvAdManager to avoid resume race after midroll',
      );
    } else {
      handleContentPlay();
    }
    logger.debug('Ad Player transition after pre roll ads');
  };

  const contentPlayerAction = (state: any): void => {
    if (state) {
      contentPlayer.unsubscribe('streamended', resetContentComplete);
      switchToAdContext();
      contentPlayer.pause();
    } else {
      contentPlayer.subscribe('streamended', resetContentComplete);
      switchToMainContext();
      // Developer Notes:
      // If autoPlayPreference is set true.
      // While switching to main context, if the content player state is still in loading
      // we would enable autoPlay if not call play API on content player because we forcibly disable autoplay before loading both players.
      // Also, we should limit the player not to play if the content is get completed.
      if (autoPlayPreference) {
        if (contentPlayer.playerState === PLAYBACK_STATES.LOADING) {
          enableAutoPlayOnLoad();
        } else if (
          contentPlayer.playerState === PLAYBACK_STATES.LOADED &&
          !contentCompleted &&
          !PlatformUtil.isSmartTV()
        ) {
          logger.debug('Ad Player transition after mid roll ads');
          contentPlayer.play();
        } else if (!contentCompleted) {
          handleMidrollOrPrerollResume();
        }
      }
      // Note:
      // When the adPlayer requests resume to the content player. If the autoPlay preference is false and content is not yet started, we need to emit the content player's current state.
      // when the content gets completed and after the end of post-roll ads, content player current state has to be emiited.
      if ((!contentStarted && !autoPlayPreference) || contentCompleted) {
        emitContentPlayerCurrentState(contentPlayer, logger, eventEmitter);
      }
    }
  };

  const subscribeAdPlayer = (player: AdPlayer): void => {
    player.subscribe('adstatechanged', (state: any) => {
      contentPlayerAction(state);
    });
  };

  const subscribeContentPlayer = (player: Player): void => {
    player.subscribe('playbackstatechanged', (state: string) => {
      if (state === PLAYBACK_STATES.STARTED) {
        contentStarted = true;
        contentCompleted = false;
      }
      logger.debug(`Content Player state: ${state}`);
    });
  };

  const composableAdPlayer: AdComposedPlayer = {
    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      adPlayer.subscribe(eventName, callback);
      contentPlayer.subscribe(eventName, callback);
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      adPlayer.unsubscribe(eventName, callback);
      contentPlayer.unsubscribe(eventName, callback);
      eventEmitter.unsubscribe(eventName, callback);
    },

    get playerContext(): PlayerContext {
      if (!isAdContext() && isAdComposedPlayer(contentPlayer)) {
        return contentPlayer.playerContext;
      }

      return internalPlayerContext;
    },

    get playerState() {
      if (isAdContext()) {
        return adPlayer.playerState;
      } else {
        return contentPlayer.playerState;
      }
    },

    get playbackRate() {
      return contentPlayer.playbackRate;
    },

    set playbackRate(rate) {
      if (!isAdContext()) {
        contentPlayer.playbackRate = rate;
      }
    },

    get availableBitrates() {
      return isAdContext() ? adPlayer.availableBitrates : contentPlayer.availableBitrates;
    },

    set minBitrate(minBitrate) {
      if (!isAdContext()) {
        contentPlayer.minBitrate = minBitrate;
      }
    },

    get minBitrate() {
      return isAdContext() ? adPlayer.minBitrate : contentPlayer.minBitrate;
    },

    set maxBitrate(maxBitrate) {
      if (!isAdContext()) {
        contentPlayer.maxBitrate = maxBitrate;
      }
    },

    get maxBitrate() {
      return isAdContext() ? adPlayer.maxBitrate : contentPlayer.maxBitrate;
    },

    get internalPlayerName() {
      return isAdContext() ? adPlayer.internalPlayerName : contentPlayer.internalPlayerName;
    },

    get internalPlayerVersion() {
      return isAdContext() ? adPlayer.internalPlayerVersion : contentPlayer.internalPlayerVersion;
    },

    get rawPlayer() {
      return isAdContext() ? undefined : contentPlayer.rawPlayer;
    },

    get rawLibrary() {
      return isAdContext() ? undefined : contentPlayer.rawLibrary;
    },

    get isBuffering() {
      return isAdContext() ? adPlayer.isBuffering : contentPlayer.isBuffering;
    },

    get isSeeking() {
      return isAdContext() ? adPlayer.isSeeking : contentPlayer.isSeeking;
    },

    get currentTime() {
      return isAdContext() ? adPlayer.currentTime : contentPlayer.currentTime;
    },

    get currentEpochTime() {
      return isAdContext() ? adPlayer.currentEpochTime : contentPlayer.currentEpochTime;
    },

    get duration() {
      return isAdContext() ? adPlayer.duration : contentPlayer.duration;
    },

    get playbackStatistics() {
      return isAdContext() ? adPlayer.playbackStatistics : contentPlayer.playbackStatistics;
    },

    get isLive() {
      return contentPlayer.isLive;
    },

    get remainingTime() {
      return isAdContext() ? adPlayer.remainingTime : -1;
    },

    get isMute() {
      return isAdContext() ? adPlayer.isMute : contentPlayer.isMute;
    },

    get playbackOptions() {
      return contentPlayer.playbackOptions ?? undefined;
    },

    get volume() {
      return isAdContext() ? adPlayer.volume : contentPlayer.volume;
    },

    set volume(volumeLevel: number) {
      adPlayer.volume = volumeLevel;
      contentPlayer.volume = volumeLevel;
    },

    get adInsertionType(): AdTechnology | undefined {
      return AdTechnology.CSAI;
    },

    load(): void {
      loadPlayers();
    },

    setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
      contentPlayer.setRequestInterceptor(requestInterceptor);
    },

    setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
      contentPlayer.setResponseInterceptor(responseInterceptor);
    },

    accessFunctionHandler(json: string): void {
      contentPlayer.accessFunctionHandler(json);
    },

    stop(): Promise<void> {
      const cleanup = (): void => {
        afcPlaybackSession?.dispose();
        afcPlaybackSession = undefined;
        adIncomingNotifier?.destroy();
        adIncomingNotifier = undefined;
      };

      if (isAdContext()) {
        return adPlayer.stop().finally(() => {
          cleanup();
          return contentPlayer.stop();
        });
      }
      cleanup();
      return contentPlayer.stop();
    },

    play(): void {
      // Reset autoPlayPreference to true if the user call Play API.
      autoPlayPreference = true;

      // When the players are in `idle` state,
      // and if `play` API is triggered, load the ad and contents players
      // Based on ad player state, we will either proceed to play the ad
      // or the content
      if (adPlayer.playerState === 'idle' && contentPlayer.playerState === 'idle') {
        // If the autoPlay is enabled the content player load will override the pause call during the ad playback and plays the main content in the background.
        loadPlayers();
        return;
      } else if (adPlayer.playerState === 'idle') {
        //Note:[QPS-2147] if the autoPlay is disabled on playbackOptions the player on loaded state, upon click on the play button adPlayer will be loaded and play.
        adPlayer.load();
        return;
      }

      if (shouldSkipPlayOperation()) {
        return;
      }

      if (isAdContext()) {
        adPlayer.play();
      } else {
        contentPlayer.play();
      }
    },

    pause(): void {
      if (shouldSkipPauseOperation()) {
        return;
      }

      if (isAdContext()) {
        adPlayer.pause();
      } else {
        contentPlayer.pause();
      }
    },

    abort: (error: Error) => {
      adPlayer.abort(error);
      contentPlayer.abort(error);
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      adPlayer.send(eventName, action, metadata);
      contentPlayer.send(eventName, action, metadata);
    },

    seek(seekPoint: number): void {
      adPlayer.seek(seekPoint);
    },

    async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      if (isAdContext()) {
        return adPlayer.getThumbnail(position);
      } else {
        return contentPlayer.getThumbnail(position);
      }
    },

    seekToLiveEdge(): void {
      if (!isAdContext()) {
        return contentPlayer.seekToLiveEdge();
      } else {
        return adPlayer.seekToLiveEdge();
      }
    },

    seekableRange(): SeekRangeInfo {
      return isAdContext() ? adPlayer.seekableRange() : contentPlayer.seekableRange();
    },

    currentOffsetFromLiveEdge(): number {
      if (!isAdContext()) {
        return contentPlayer.currentOffsetFromLiveEdge();
      } else {
        return adPlayer.currentOffsetFromLiveEdge();
      }
    },

    getAllTracks(): TrackInfo[] {
      return isAdContext() ? [] : contentPlayer.getAllTracks();
    },

    getActiveTracks(): TrackInfo[] {
      return isAdContext() ? [] : contentPlayer.getActiveTracks();
    },

    getVariantTracks(): TrackVariantInfo[] {
      return contentPlayer.getVariantTracks();
    },

    getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
      return contentPlayer.getAvailableTrackVariants(trackType);
    },

    selectTrack(track?: TrackInfo): void {
      if (!isAdContext()) {
        contentPlayer.selectTrack(track);
      } else {
        // no-op
      }
    },

    muteOrUnmute(): void {
      adPlayer.muteOrUnmute && adPlayer.muteOrUnmute();
      contentPlayer.muteOrUnmute && contentPlayer.muteOrUnmute();
    },

    skipAd(): void {
      if (isAdContext()) {
        adPlayer.skipAd();
      } else {
        logger.warn(`Unsupported operation 'skipAd' in ${internalPlayerContext} context`);
      }
    },

    resize(width: number, height: number, isFullscreen: boolean): void {
      adPlayer.resize(width, height, isFullscreen);
    },

    discardAdBreak(): void {
      if (isAdContext()) {
        adPlayer.discardAdBreak();
      } else {
        logger.warn(`Unsupported operation 'discardAdBreak' in ${internalPlayerContext} context`);
      }
    },

    getCuePoints(): number[] {
      return adPlayer.getCuePoints();
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      contentPlayer.setPlaybackOptions(playbackOptions);
    },

    restrictQuality(enableRestriction: boolean): void {
      contentPlayer.restrictQuality(enableRestriction);
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return contentPlayer.availableMimeTypes(trackType);
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      return contentPlayer.setPreferredMimeTypes(trackType, mimeTypes);
    },

    unloadAndSavePreload(): Promise<void> {
      // no-op
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      // no-op
    },
  };

  subscribeAdPlayer(adPlayer);
  subscribeContentPlayer(contentPlayer);

  afcPlaybackSession = createComposableAFCPlaybackSession(builder, composableAdPlayer, adPlayer);

  if (!composableAdPlayer.isLive) {
    adIncomingNotifier = new AdIncomingNotifier(
      composableAdPlayer,
      eventEmitter,
      builder?.configuredIncomingLeadTimeMs,
      builder?.configuredLogger,
    );
    builder?.configuredLogger?.log('AdIncomingNotifier initialized:');
  }

  return composableAdPlayer;
}
